import { Box, Grid, Skeleton, Typography } from "@mui/material";
import { Formik } from "formik";
import { useEffect, useMemo, useState } from "react";
import * as yup from "yup";
import { showErrorNotification, showSuccessNotification, showWarningNotification } from "../../../components/admin/common/NotiService";
import OZDialog from "../../../components/admin/common/OZDialog";
import OZFCheckbox from "../../../components/admin/common/OZFCheckbox";
import OZInput from "../../../components/admin/common/OZInput";
import LoadingScreen from "../../../components/admin/common/OZLoadingScreen/OZLoadingScreen";
import OZSelect from "../../../components/admin/common/OZSelect";
import { getPriDetailsList, saveDIDAddition } from "../../../services/api-service";
import { ADMIN_FORM_MODES } from "../../../utils/constants";

const APP_CODE_OPTIONS = [{ id: "CC", label: "CloudAgent" }, { id: "NC", label: "KooKoo" }];
const getResponseStatus = (response) => response?.status || response?.Status;
const getResponseMessage = (response) => response?.message || response?.Message;
const getResponseData = (response) => response?.data || response?.Data;
const isSuccessResponse = (response) => getResponseStatus(response) === "Success";
const getAppCodeOption = (appCode) => {
    if (!appCode) return APP_CODE_OPTIONS[0];
    if (typeof appCode === "object") {
        return APP_CODE_OPTIONS.find((option) => option.id === appCode.id) || APP_CODE_OPTIONS[0];
    }
    return APP_CODE_OPTIONS.find((option) => option.id === appCode) || APP_CODE_OPTIONS[0];
};
const normalizeEditData = (editData) => ({
    ...editData,
    pRing: typeof editData?.pRing === "boolean" ? editData.pRing : Boolean(editData?.pring),
    appCode: getAppCodeOption(editData?.appCode),
    countryCode: editData?.countryCode || "",
    phoneType: editData?.phoneType || "Local",
});

const DIDAdditionForm = (props) => {
    const mode = props.data ? ADMIN_FORM_MODES.edit : ADMIN_FORM_MODES.add;
    const [showForm, setShowForm] = useState(true);
    const [priOptions, setPriOptions] = useState([]);
    const [priLoading, setPriLoading] = useState(false);

    useEffect(() => {
        fetchPriDetails();
    }, []);

    const fetchPriDetails = async () => {
        setPriLoading(true);
        try {
            const resp = await getPriDetailsList();
            if (isSuccessResponse(resp)) {
                setPriOptions(getResponseData(resp) || []);
            } else {
                setPriOptions([]);
                showWarningNotification(getResponseMessage(resp) || "Unable to fetch PRI details");
            }
        } catch (e) {
            setPriOptions([]);
            showErrorNotification("Error fetching PRI details");
        }
        setPriLoading(false);
    };

    const validationSchema = useMemo(
        () =>
            yup.object({
                number: yup
                    .string()
                    .required("Number is required")
                    .matches(/^((\+)?\d{3,16})$/, "DID should be between 3 to 16 digits long and allows prefix +"),
                priDetails: yup
                    .object()
                    .nullable()
                    .required("PRI is required"),
                maxCalls: yup
                    .number()
                    .typeError("Max Calls should be between 1 and 5000.")
                    .min(1, "Max Calls should be between 1 and 5000.")
                    .max(5000, "Max Calls should be between 1 and 5000.")
                    .nullable(),
                timeout: yup
                    .number()
                    .typeError("Time Out should be between 1 and 100000.")
                    .min(1, "Time Out should be between 1 and 100000.")
                    .max(100000, "Time Out should be between 1 and 100000.")
                    .nullable(),
                appCode: yup
                    .object()
                    .nullable()
                    .required("App Code is required"),
                callerId: yup
                    .string()
                    .required("Caller ID is required")
                    .matches(/^((\+)?\d{3,16})$/, "Caller ID should be between 3 to 16 digits long and allows prefix +"),
                countryCode: yup
                    .string()
                    .nullable()
                    .min(2, "Country Code should be between 2 to 255 alphanumeric characters long and allows special characters like _,spaces and it cannot start and end with Special characters.")
                    .max(255, "Country Code should be between 2 to 255 alphanumeric characters long and allows special characters like _,spaces and it cannot start and end with Special characters.")
                    .matches(/^[A-Za-z0-9](?:[A-Za-z0-9_ ]*[A-Za-z0-9])$/, "Country Code should be between 2 to 255 alphanumeric characters long and allows special characters like _,spaces and it cannot start and end with Special characters."),
                phoneType: yup
                    .string()
                    .required("Phone Type is required"),
            }),
        []
    );

    const closeForm = () => {
        props.onClose();
        setShowForm(false);
    };

    const handleSubmit = async (values) => {
        try {
            let payload = { ...values };
            // Transform priDetails to { id: priDetails.id }
            if (payload.priDetails) {
                payload.priDetails = { id: payload.priDetails.id };
            }
            if (payload.appCode) {
                payload.appCode = payload.appCode.id || payload.appCode;
            }
            if (typeof payload.pRing === "boolean") {
                payload.pring = payload.pRing;
            }
            // For create, remove id
            if (mode === ADMIN_FORM_MODES.add) {
                delete payload.id;
            }

            const resp = await saveDIDAddition(payload);
            if (isSuccessResponse(resp)) {
                showSuccessNotification(
                    mode === ADMIN_FORM_MODES.add
                        ? "DID Addition created successfully"
                        : "DID Addition updated successfully"
                );
                props.refreshList();
                closeForm();
            } else {
                showWarningNotification(getResponseMessage(resp) || "Failed to save DID Addition");
            }
        } catch (e) {
            showErrorNotification("Error saving DID Addition");
        }
    };

    const DataForm = (formProps) => {
        const [formLoader] = useState(false);
        const defaultData = {
            number: "",
            priDetails: null,
            maxCalls: "",
            timeout: "",
            pRing: false,
            appCode: APP_CODE_OPTIONS[0],
            callerId: "",
            countryCode: "",
            phoneType: "Local",
        };
        const initData = formProps.editData ? normalizeEditData(formProps.editData) : defaultData;

        return (
            <div>
                <Formik
                    initialValues={initData}
                    enableReinitialize
                    validationSchema={validationSchema}
                    onSubmit={handleSubmit}
                >
                    {(formikProps) => (
                        <form
                            onSubmit={formikProps.handleSubmit}
                            id="didAdditionForm"
                            style={{ width: "600px" }}
                        >
                            <Grid container spacing={2}>
                                <Grid item xs={6}>
                                    <OZInput
                                        name="number"
                                        placeholder="Enter Number"
                                        label="Number *"
                                    />
                                </Grid>
                                <Grid item xs={6} sx={{ display: 'flex', alignItems: 'center', paddingTop: '24px !important' }}>
                                    <OZFCheckbox
                                        labelPosition="end"
                                        name="pRing"
                                        label="Pring"
                                    />
                                </Grid>
                                <Grid item xs={6}>
                                    {priLoading ? (
                                        <Box>
                                            <Typography fontSize="12px" marginBottom="6px" marginLeft="1px" color="#212121">
                                                PRI *
                                            </Typography>
                                            <Skeleton variant="rectangular" height={44} sx={{ borderRadius: "8px" }} />
                                        </Box>
                                    ) : (
                                        <OZSelect
                                            name="priDetails"
                                            label="PRI *"
                                            placeholder="Select PRI"
                                            options={priOptions}
                                            optionLabel="priName"
                                        />
                                    )}
                                </Grid>
                                <Grid item xs={6}>
                                    <OZSelect
                                        name="appCode"
                                        label="App Code *"
                                        placeholder="Select App Code"
                                        options={APP_CODE_OPTIONS}
                                        optionLabel='label'
                                    />
                                </Grid>
                                <Grid item xs={6}>
                                    <OZInput
                                        name="maxCalls"
                                        placeholder="Enter Max Calls"
                                        label="Max Calls"
                                    />
                                </Grid>
                                <Grid item xs={6}>
                                    <OZInput
                                        name="callerId"
                                        placeholder="Enter Caller ID"
                                        label="Caller ID *"
                                    />
                                </Grid>
                                <Grid item xs={6}>
                                    <OZInput
                                        name="timeout"
                                        placeholder="Enter Time Out"
                                        label="Time Out"
                                    />
                                </Grid>
                                <Grid item xs={6}>
                                    <OZInput
                                        name="countryCode"
                                        placeholder="Enter Country Code"
                                        label="Country Code"
                                    />
                                </Grid>
                                <Grid item xs={6}>
                                </Grid>
                                <Grid item xs={6}>
                                    <OZSelect
                                        name="phoneType"
                                        label="Phone Type *"
                                        placeholder="Select Phone Type"
                                        options={['Local', 'National', 'Toll Free']}
                                    />
                                </Grid>
                            </Grid>
                            {formLoader && (
                                <Box sx={{ position: 'absolute', top: 0, left: 0, right: 0, bottom: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', backgroundColor: 'rgba(255,255,255,0.7)', zIndex: 10 }}>
                                    <LoadingScreen />
                                </Box>
                            )}
                        </form>
                    )}
                </Formik>
            </div>
        );
    };

    return (
        <div>
            {mode === ADMIN_FORM_MODES.add ? (
                <OZDialog
                    open={showForm}
                    onClose={closeForm}
                    title="Add DID"
                    saveButtonProps={{ form: "didAdditionForm", type: "submit" }}
                    mode={mode}
                >
                    <DataForm />
                </OZDialog>
            ) : (
                <OZDialog
                    open={showForm}
                    onClose={closeForm}
                    title="Edit DID"
                    saveButtonProps={{ form: "didAdditionForm", type: "submit" }}
                    mode={mode}
                    hideSaveButton={!props.editAccess}
                >
                    <DataForm editData={props.data} />
                </OZDialog>
            )}
        </div>
    );
};

export default DIDAdditionForm;
